import {
  formatFolderUploadPanelCopy as fill,
  type FolderUploadPanelCopy,
} from '@/i18n/folder-upload-panel-copy';
import {
  summarizeFolderUploadJob,
  type FolderUploadJobPhase,
} from '@/lib/uploads/folder-upload/summary';
import type {
  FolderUploadFileState,
  FolderUploadJobState,
} from '@/lib/uploads/folder-upload/types';
import { formatWorkspaceFileSize } from '@/lib/format-workspace-file-size';
import { isUserHomeWorkspacePath } from '@/lib/workspace-panel-display';

/** Sizes read exactly as in the Files table beside the panel (`28 KB`). */
export function formatFolderUploadBytes(bytes: number, locale: string): string {
  return formatWorkspaceFileSize(Math.max(0, bytes), locale) ?? '';
}

/** Beyond this an estimate is not shown: the percent is honest instead. */
const CREDIBLE_ETA_SECONDS = 3600;

/** `40 sec`, `12 min`, `3 hr` (whole hours) in the member's locale. */
export function formatFolderUploadDuration(
  seconds: number,
  locale: string
): string {
  const [value, unit]: [number, 'second' | 'minute' | 'hour'] =
    seconds < 60
      ? [Math.max(1, Math.ceil(seconds)), 'second']
      : seconds < 3600
        ? [Math.ceil(seconds / 60), 'minute']
        : [Math.round(seconds / 3600), 'hour'];
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit,
    unitDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(value);
}

export function formatFolderUploadCount(count: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(count);
}

/** The folder a job lands in: `My Drive` for a member's home root. */
export function folderUploadTargetLabel(
  job: Pick<FolderUploadJobState, 'orgSlug' | 'targetDirectoryPath'>,
  myDriveLabel: string
): string {
  const segments = job.targetDirectoryPath.split('/').filter(Boolean);
  if (
    segments.length === 3 &&
    isUserHomeWorkspacePath(job.targetDirectoryPath, job.orgSlug)
  )
    return myDriveLabel;
  return segments.at(-1) ?? job.targetDirectoryPath;
}

export type FolderUploadJobView = {
  phase: FolderUploadJobPhase;
  title: string;
  /** Running: files and bytes. Finished: uploaded/failed/cancelled. */
  summary: string;
  /** Running: time left, else the percent; anchors the footer's left side. */
  progressNote: string | null;
  /** Cancel all was pressed and files are still settling. */
  cancelling: boolean;
  /** "Retry failed", or "Retry 2" when only some failures can be retried. */
  retryLabel: string;
  percent: number;
  failedCount: number;
  emptyFoldersNote: string | null;
  canCancelAll: boolean;
  canDismiss: boolean;
  canRetryFailed: boolean;
};

export function folderUploadJobView(
  job: FolderUploadJobState,
  args: {
    copy: FolderUploadPanelCopy;
    locale: string;
    myDriveLabel: string;
    now: number;
  }
): FolderUploadJobView {
  const { copy, locale } = args;
  const summary = summarizeFolderUploadJob(job, args.now);
  const folder = folderUploadTargetLabel(job, args.myDriveLabel);
  const count = (value: number) => formatFolderUploadCount(value, locale);
  const titles: Record<FolderUploadJobPhase, string> = {
    running: copy.titleUploading,
    done: copy.titleDone,
    failed: copy.titleFailed,
    cancelled: copy.titleCancelled,
    blocked: copy.titleBlocked,
  };
  // Cancel all pressed while files settle: say so, and sum up what is
  // known instead of a running total that has dropped to "0 of 0" (TASTE).
  const cancelling = summary.phase === 'running' && job.cancelRequested;
  let text: string;
  if (summary.phase === 'running' && !cancelling) {
    // Files count once their bytes are sent, so the count keeps pace with
    // the bar while the server files them ("Finishing…", TASTE @9fa2ed48).
    text = fill(copy.progressSummary, {
      done: count(summary.filesSent),
      total: count(summary.filesTotal),
      bytesDone: formatFolderUploadBytes(summary.bytesDone, locale),
      bytesTotal: formatFolderUploadBytes(summary.bytesTotal, locale),
    });
  } else {
    const uploaded =
      summary.filesDone > 0
        ? fill(copy.summaryUploaded, { count: count(summary.filesDone) })
        : null;
    const failed =
      summary.filesFailed > 0
        ? fill(copy.summaryFailed, { count: count(summary.filesFailed) })
        : null;
    const cancelled =
      summary.filesCancelled > 0
        ? fill(copy.summaryCancelled, { count: count(summary.filesCancelled) })
        : null;
    // A cancelled upload leads with what the member asked for.
    text = (
      summary.phase === 'cancelled' || cancelling
        ? [uploaded, cancelled, failed]
        : [uploaded, failed, cancelled]
    )
      .filter(Boolean)
      .join(' · ');
  }
  // The percent always leads, so the note never swaps format; time left
  // follows only when credible (a stall makes the estimate explode).
  const credibleEta =
    summary.etaSeconds !== null && summary.etaSeconds <= CREDIBLE_ETA_SECONDS
      ? summary.etaSeconds
      : null;
  const percentText = new Intl.NumberFormat(locale, {
    style: 'percent',
  }).format(summary.fraction);
  const progressNote =
    summary.phase !== 'running' || cancelling
      ? null
      : credibleEta !== null
        ? `${percentText} · ${fill(copy.timeLeft, {
            time: formatFolderUploadDuration(credibleEta, locale),
          })}`
        : percentText;
  return {
    phase: summary.phase,
    title: fill(cancelling ? copy.titleCancelling : titles[summary.phase], {
      folder,
    }),
    summary: text,
    progressNote,
    cancelling,
    retryLabel:
      summary.retryableFailedCount < summary.filesFailed
        ? fill(copy.retrySome, { count: count(summary.retryableFailedCount) })
        : copy.retryFailed,
    percent: Math.round(summary.fraction * 100),
    failedCount: summary.filesFailed,
    emptyFoldersNote:
      job.emptyFolders.failed > 0
        ? fill(copy.emptyFoldersFailed, {
            count: count(job.emptyFolders.failed),
          })
        : null,
    canCancelAll: summary.phase === 'running',
    canDismiss: summary.phase !== 'running',
    // Empty folders that failed to appear are retried too (review S1).
    canRetryFailed:
      summary.phase !== 'running' &&
      (summary.retryableFailedCount > 0 || job.emptyFolders.failed > 0),
  };
}

export type FolderUploadRowView = {
  /** Status line under the name: progress, reason or state. */
  detail: string;
  tone: 'muted' | 'attention' | 'success';
  percent: number | null;
  canCancel: boolean;
  canRetry: boolean;
};

export function folderUploadRowView(
  file: FolderUploadFileState,
  copy: FolderUploadPanelCopy,
  locale: string
): FolderUploadRowView {
  const base = { percent: null, canCancel: false, canRetry: false };
  switch (file.status) {
    case 'queued':
      return {
        ...base,
        detail: copy.statusQueued,
        tone: 'muted',
        canCancel: true,
      };
    case 'uploading':
      // Not started yet: "Waiting", never "0 B of 25 MB".
      if (file.loadedBytes === 0)
        return {
          ...base,
          detail: copy.statusQueued,
          tone: 'muted',
          canCancel: true,
        };
      return {
        detail: fill(copy.statusUploading, {
          loaded: formatFolderUploadBytes(file.loadedBytes, locale),
          total: formatFolderUploadBytes(file.sizeBytes, locale),
        }),
        tone: 'muted',
        percent:
          file.sizeBytes > 0
            ? Math.round((file.loadedBytes / file.sizeBytes) * 100)
            : 0,
        canCancel: true,
        canRetry: false,
      };
    case 'registering':
      return { ...base, detail: copy.statusRegistering, tone: 'muted' };
    case 'done':
      return {
        ...base,
        detail: `${copy.statusDone} · ${formatFolderUploadBytes(file.sizeBytes, locale)}`,
        tone: 'success',
      };
    case 'cancelled':
      return {
        ...base,
        detail: copy.statusCancelled,
        tone: 'muted',
        canRetry: file.retryable,
      };
    case 'failed':
      return {
        ...base,
        detail: copy.reasons[file.reason ?? 'unknown'],
        tone: 'attention',
        canRetry: file.retryable,
      };
  }
}

const ROW_ORDER: Record<FolderUploadFileState['status'], number> = {
  uploading: 0,
  registering: 0,
  failed: 1,
  queued: 2,
  cancelled: 3,
  done: 4,
};

/**
 * The rows the list shows: what is moving first, then what needs attention,
 * then the rest; or only the failed files.
 */
export function folderUploadRows(
  files: ReadonlyArray<FolderUploadFileState>,
  filter: 'all' | 'failed'
): FolderUploadFileState[] {
  if (filter === 'failed')
    return files.filter((file) => file.status === 'failed');
  return [...files].sort(
    (a, b) => ROW_ORDER[a.status] - ROW_ORDER[b.status] || a.id - b.id
  );
}
